import React from "react";

/**
 * PSHL∞ Official Wordmark — 3-variant brand pack
 *
 * variant:
 *   "primary"  — Silver-chrome PSHL + metallic-gold ∞   (use on dark backgrounds)
 *   "dark"     — Dark/charcoal PSHL + solid gold ∞       (use on light backgrounds)
 *   "mono"     — Single-color version                    (color prop controls fill; default = currentColor)
 *
 * Scales to any size via `className` (height controls everything).
 */
export default function PshlWordmark({
  className = "h-10 w-auto",
  variant = "primary",
  color, // optional explicit color for mono variant
  title = "PSHL8 — Pro Skate Hockey League",
}) {
  const uid = React.useId();
  const silverId = `silver-${uid}`;
  const silverEdgeId = `silver-edge-${uid}`;
  const goldId = `gold-${uid}`;
  const goldEdgeId = `gold-edge-${uid}`;
  const shineId = `shine-${uid}`;
  const darkId = `dark-${uid}`;
  const darkEdgeId = `dark-edge-${uid}`;

  // Per-variant fills / strokes
  const cfg = (() => {
    if (variant === "dark") {
      return {
        textFill: `url(#${darkId})`,
        textStroke: `url(#${darkEdgeId})`,
        textStrokeWidth: 1.2,
        shineFill: null, // no white shine on dark variant
        infinityStroke: `url(#${goldId})`,
        infinityEdge: `url(#${goldEdgeId})`,
        infinityHighlight: "#FFE9A8",
        highlightOpacity: 0.45,
      };
    }
    if (variant === "mono") {
      const c = color || "currentColor";
      return {
        textFill: c,
        textStroke: "none",
        textStrokeWidth: 0,
        shineFill: null,
        infinityStroke: c,
        infinityEdge: "none",
        infinityHighlight: null,
        highlightOpacity: 0,
        infinityEdgeWidth: 0,
      };
    }
    // primary (default)
    return {
      textFill: `url(#${silverId})`,
      textStroke: `url(#${silverEdgeId})`,
      textStrokeWidth: 1.2,
      shineFill: `url(#${shineId})`,
      infinityStroke: `url(#${goldId})`,
      infinityEdge: `url(#${goldEdgeId})`,
      infinityHighlight: "#FFF7D0",
      highlightOpacity: 0.55,
    };
  })();

  return (
    <svg
      viewBox="0 0 410 120"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={title}
      className={className}
      preserveAspectRatio="xMidYMid meet"
    >
      <title>{title}</title>
      <defs>
        {/* Silver / chrome gradient */}
        <linearGradient id={silverId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor="#FFFFFF" />
          <stop offset="18%"  stopColor="#E6E8EC" />
          <stop offset="40%"  stopColor="#9A9DA5" />
          <stop offset="52%"  stopColor="#3C3F46" />
          <stop offset="60%"  stopColor="#5A5E66" />
          <stop offset="80%"  stopColor="#BDC0C6" />
          <stop offset="100%" stopColor="#F0F2F5" />
        </linearGradient>
        <linearGradient id={silverEdgeId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor="#FFFFFF" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#0F1115" stopOpacity="0.95" />
        </linearGradient>
        {/* Dark / graphite gradient for light backgrounds */}
        <linearGradient id={darkId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor="#1A1D24" />
          <stop offset="50%"  stopColor="#05060A" />
          <stop offset="100%" stopColor="#2B2F38" />
        </linearGradient>
        <linearGradient id={darkEdgeId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor="#000000" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0.9" />
        </linearGradient>
        {/* Gold gradient */}
        <linearGradient id={goldId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor="#FCF6BA" />
          <stop offset="22%"  stopColor="#E2B843" />
          <stop offset="48%"  stopColor="#8C611A" />
          <stop offset="60%"  stopColor="#B38728" />
          <stop offset="82%"  stopColor="#F8E27A" />
          <stop offset="100%" stopColor="#FBF5B7" />
        </linearGradient>
        <linearGradient id={goldEdgeId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor="#FFE9A8" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#3A2807" stopOpacity="0.95" />
        </linearGradient>
        <linearGradient id={shineId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor="#FFFFFF" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* PSHL text */}
      <g>
        <text
          x="0"
          y="92"
          fontFamily="Oswald, 'Arial Narrow', Impact, sans-serif"
          fontWeight="700"
          fontSize="110"
          letterSpacing="-4"
          fill={cfg.textFill}
          stroke={cfg.textStroke}
          strokeWidth={cfg.textStrokeWidth}
          paintOrder="stroke fill"
        >
          PSHL
        </text>
        {cfg.shineFill && (
          <text
            x="0"
            y="92"
            fontFamily="Oswald, 'Arial Narrow', Impact, sans-serif"
            fontWeight="700"
            fontSize="110"
            letterSpacing="-4"
            fill={cfg.shineFill}
            clipPath="inset(0 0 70% 0)"
            aria-hidden="true"
          >
            PSHL
          </text>
        )}
      </g>

      {/* Infinity ∞ */}
      <g transform="translate(245, 30)">
        <path
          d="M 10 35
             C 10 5,  60 5,  72 35
             C 84 65, 134 65, 134 35
             C 134 5,  84 5,  72 35
             C 60 65, 10 65, 10 35 Z"
          fill="none"
          stroke={cfg.infinityStroke}
          strokeWidth="26"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {cfg.infinityEdge && cfg.infinityEdge !== "none" && (
          <path
            d="M 10 35
               C 10 5,  60 5,  72 35
               C 84 65, 134 65, 134 35
               C 134 5,  84 5,  72 35
               C 60 65, 10 65, 10 35 Z"
            fill="none"
            stroke={cfg.infinityEdge}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.55"
          />
        )}
        {cfg.infinityHighlight && (
          <>
            <path
              d="M 22 28 C 24 14, 58 14, 70 30"
              fill="none"
              stroke={cfg.infinityHighlight}
              strokeWidth="3"
              strokeLinecap="round"
              opacity={cfg.highlightOpacity}
            />
            <path
              d="M 78 30 C 90 14, 124 14, 124 28"
              fill="none"
              stroke={cfg.infinityHighlight}
              strokeWidth="3"
              strokeLinecap="round"
              opacity={cfg.highlightOpacity}
            />
          </>
        )}
      </g>
    </svg>
  );
}
